import { useCallback, useMemo } from 'react';
import { generatePath, matchPath } from 'react-router-dom';
import {
  buildQueryFilters,
  PermissionAction,
  PermissionResource,
  useGetChildOrganizations,
  useGetOrganization,
} from '@sib/api';
import { DEFAULT_PAGE, MAXIMUM_PAGINATION } from '@sib/api/constants';
import { useCan, useUserSetupStore } from '@sib/auth';
import { paths } from '@spendbrain/config/paths';
import { usePowerBiReports } from './use-power-bi-reports';

export const useSpendbrainRoutesAccess = () => {
  const { powerBiReports } = usePowerBiReports();
  const showReportsDirectory = powerBiReports.length > 0;
  const canAccess = useCan();
  const {
    organization: { id: organizationId },
    hasGlobalAccess,
  } = useUserSetupStore();

  const ticketsPath = useMemo(
    () =>
      hasGlobalAccess
        ? generatePath(paths.tickets.employee.root, { orgId: organizationId })
        : paths.tickets.client.root,
    [hasGlobalAccess, organizationId]
  );
  const {
    data: organizationData,
    isLoading,
    isPending,
  } = useGetOrganization(organizationId);

  const isOrganizationDataLoading = isLoading || isPending;

  const hasVectorClientId = !!organizationData?.vectorClientId;
  const hasNetsuiteCustomerId = !!organizationData?.netsuiteCustomerId;
  const hasBillingAccess = hasVectorClientId || hasNetsuiteCustomerId;

  const { data: childOrgsData } = useGetChildOrganizations(
    organizationId || '',
    {
      page: DEFAULT_PAGE,
      size: MAXIMUM_PAGINATION,
      filter: buildQueryFilters([
        { field: 'micrositesEnabled', operator: 'eq', value: true },
      ]),
    }
  );

  const hasChildren = (childOrgsData?.totalItems ?? 0) > 0;

  const availableRoutes = useMemo(
    () =>
      [
        ...(hasChildren
          ? [
              {
                href: paths.subsidiaryOrganization,
              },
            ]
          : []),
        ...(canAccess({
          permissions: [
            {
              actions: PermissionAction.READ,
              resources: PermissionResource.SpendAreas,
            },
            {
              actions: PermissionAction.READ,
              resources: PermissionResource.ActionItems,
            },
            {
              actions: PermissionAction.CREATE,
              resources: PermissionResource.SpendAreas,
            },
          ],
          operator: 'OR',
        })
          ? [
              {
                href: paths.dashboard,
                access: {
                  permissions: {
                    actions: PermissionAction.READ,
                    resources: PermissionResource.Dashboard,
                  },
                },
              },
            ]
          : []),
        {
          href: paths.savings,
          access: {
            permissions: {
              actions: PermissionAction.READ,
              resources: PermissionResource.Savings,
            },
          },
        },
        {
          href: paths.services,
          access: {
            permissions: [
              {
                actions: PermissionAction.READ,
                resources: PermissionResource.SpendAreas,
              },
              {
                actions: PermissionAction.READ,
                resources: PermissionResource.ActionItems,
              },
              {
                actions: PermissionAction.CREATE,
                resources: PermissionResource.SpendAreas,
              },
            ],
            operator: 'OR',
          },
        },
        {
          href: paths.documents.any,
          access: {
            permissions: {
              actions: PermissionAction.READ,
              resources: PermissionResource.Documents,
            },
          },
        },
        {
          href: paths.locations,
          access: {
            permissions: {
              actions: PermissionAction.READ,
              resources: PermissionResource.Locations,
            },
          },
        },

        ...(showReportsDirectory ? [{ href: paths.reports }] : []),
        {
          href: ticketsPath,
          access: {
            permissions: [
              {
                actions: PermissionAction.READ,
                resources: PermissionResource.Tickets,
              },
              {
                actions: PermissionAction.CREATE,
                resources: PermissionResource.Tickets,
              },
            ],
            operator: 'OR',
          },
        },
        {
          href: paths.tickets.employee.details,
          access: {
            permissions: [
              {
                actions: PermissionAction.READ,
                resources: PermissionResource.Tickets,
              },
              {
                actions: PermissionAction.CREATE,
                resources: PermissionResource.Tickets,
              },
            ],
            operator: 'OR',
          },
        },
        {
          href: paths.upcomingEvents,
        },
        {
          href: paths.chat,
        },
        {
          href: paths.notFound,
        },
        {
          href: paths.yourTeam,
          access: {
            permissions: {
              actions: PermissionAction.READ,
              resources: PermissionResource.Teams,
            },
          },
        },
        {
          href: paths.account,
        },
        ...(hasBillingAccess
          ? [
              {
                href: paths.billing,
              },
            ]
          : []),
        {
          href: paths.vendors,
          access: {
            permissions: {
              actions: PermissionAction.READ,
              resources: PermissionResource.Vendors,
            },
          },
        },
        {
          href: paths.billPay,
        },
        {
          href: paths.contractTerms,
        },
        {
          href: paths.help,
        },
        {
          href: 'https://aboutsib.com/',
        },
      ].filter(
        ({ access }) =>
          !access || canAccess(access as Parameters<typeof canAccess>[0])
      ),
    [
      canAccess,
      hasBillingAccess,
      hasChildren,
      showReportsDirectory,
      ticketsPath,
    ]
  );

  const firstRouteAvailable = useMemo(
    () => availableRoutes[0].href,
    [availableRoutes]
  );

  const isRouteAvailable = useCallback(
    (path: string) =>
      isOrganizationDataLoading ||
      availableRoutes.some(route => route.href === path) ||
      availableRoutes.some(
        route =>
          matchPath({ path }, route.href) ||
          matchPath({ path: route.href }, path)
      ),
    [availableRoutes, isOrganizationDataLoading]
  );

  return { availableRoutes, isRouteAvailable, firstRouteAvailable };
};
